/**
 * BrandingTab — Admin page tab to configure global branding (white-label).
 * Logo, name, tagline, favicon, theme/accent colors, footer text.
 */
import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, Save, Loader2, Image as ImageIcon, Palette, Smartphone, Eye } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';

const ASSET_LABELS = {
  logo: { label: 'App Logo', hint: 'Header + login. Square PNG/SVG, ideally 256×256px.' },
  favicon: { label: 'Favicon', hint: 'Browser tab icon. PNG/ICO, 32×32 or 64×64.' },
  icon_192: { label: 'PWA Icon (192×192)', hint: 'Home-screen icon. PNG, 192×192.' },
  icon_512: { label: 'PWA Icon (512×512)', hint: 'Splash + install. PNG, 512×512.' },
};

function AssetUploader({ assetType, currentUrl, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.post(`/admin/branding/upload-asset?asset_type=${assetType}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(resp.data.branding);
      toast.success(`${ASSET_LABELS[assetType].label} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border-2 border-border" data-testid={`asset-${assetType}`}>
      <div className="w-14 h-14 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
        {currentUrl ? (
          <img src={brandingAssetUrl(currentUrl)} alt="" className="w-full h-full object-contain" />
        ) : (
          <ImageIcon className="w-6 h-6 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{ASSET_LABELS[assetType].label}</p>
        <p className="text-xs text-muted-foreground">{ASSET_LABELS[assetType].hint}</p>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,.svg,.ico"
        className="hidden"
        onChange={handleSelect}
        data-testid={`asset-${assetType}-input`}
      />
      <Button
        variant="outline"
        size="sm"
        className="rounded-full shrink-0"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        data-testid={`asset-${assetType}-btn`}
      >
        {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
        {uploading ? 'Uploading' : 'Upload'}
      </Button>
    </div>
  );
}

export default function BrandingTab() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({
    app_name: '',
    tagline: '',
    theme_color: '#143D30',
    accent_color: '#E05D3A',
    footer_text: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      app_name: branding.app_name || '',
      tagline: branding.tagline || '',
      theme_color: branding.theme_color || '#143D30',
      accent_color: branding.accent_color || '#E05D3A',
      footer_text: branding.footer_text || '',
    });
  }, [branding]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/branding', form);
      await refresh();
      toast.success('Branding updated. Changes are live.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update branding');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-5"
      data-testid="branding-tab-content"
    >
      {/* Live preview chip */}
      <Card className="p-4 border-2 rounded-2xl bg-primary/5">
        <div className="flex items-center gap-3">
          <Eye className="w-4 h-4 text-primary" />
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Live preview</p>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <div className="w-12 h-12 rounded-xl bg-card border-2 flex items-center justify-center overflow-hidden">
            {branding.logo_url && (
              <img src={brandingAssetUrl(branding.logo_url)} alt="" className="w-10 h-10 object-contain" />
            )}
          </div>
          <div>
            <p className="font-display text-lg font-bold">{form.app_name || 'Your App'}</p>
            <p className="text-xs text-muted-foreground">{form.tagline || 'Your tagline shows here'}</p>
          </div>
        </div>
      </Card>

      {/* Text fields */}
      <Card className="p-5 border-2 rounded-2xl space-y-4">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-primary" />
          <h3 className="font-display font-bold">Identity</h3>
        </div>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">App Name</Label>
            <Input
              value={form.app_name}
              onChange={(e) => update('app_name', e.target.value)}
              maxLength={40}
              placeholder="SplitEasy"
              data-testid="branding-app-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Tagline</Label>
            <Input
              value={form.tagline}
              onChange={(e) => update('tagline', e.target.value)}
              maxLength={120}
              placeholder="Track expenses with friends"
              data-testid="branding-tagline"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Footer Text</Label>
            <Input
              value={form.footer_text}
              onChange={(e) => update('footer_text', e.target.value)}
              maxLength={120}
              placeholder="© Your Company"
              data-testid="branding-footer"
            />
          </div>
        </div>
      </Card>

      {/* Colors */}
      <Card className="p-5 border-2 rounded-2xl space-y-4">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary" />
          <h3 className="font-display font-bold">Colors</h3>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Theme</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.theme_color}
                onChange={(e) => update('theme_color', e.target.value)}
                className="w-12 h-10 rounded-lg border-2 cursor-pointer"
                data-testid="branding-theme-color"
              />
              <Input
                value={form.theme_color}
                onChange={(e) => update('theme_color', e.target.value)}
                pattern="^#[0-9a-fA-F]{6}$"
                className="flex-1"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider">Accent</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.accent_color}
                onChange={(e) => update('accent_color', e.target.value)}
                className="w-12 h-10 rounded-lg border-2 cursor-pointer"
                data-testid="branding-accent-color"
              />
              <Input
                value={form.accent_color}
                onChange={(e) => update('accent_color', e.target.value)}
                pattern="^#[0-9a-fA-F]{6}$"
                className="flex-1"
              />
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Colors apply to manifest theme + status bar on installed PWAs. Reload to see changes everywhere.
        </p>
      </Card>

      {/* Assets */}
      <Card className="p-5 border-2 rounded-2xl space-y-3">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-primary" />
          <h3 className="font-display font-bold">Assets</h3>
        </div>
        <div className="space-y-3">
          <AssetUploader assetType="logo" currentUrl={branding.logo_url} onUploaded={refresh} />
          <AssetUploader assetType="favicon" currentUrl={branding.favicon_url} onUploaded={refresh} />
          <AssetUploader assetType="icon_192" currentUrl={branding.icon_192_url} onUploaded={refresh} />
          <AssetUploader assetType="icon_512" currentUrl={branding.icon_512_url} onUploaded={refresh} />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Note: users who already installed the app on iOS will see the new icon only after they
          remove and re-add the home-screen shortcut (a Web Platform limitation).
        </p>
      </Card>

      <Button
        onClick={onSave}
        disabled={saving}
        className="w-full rounded-full press-scale h-12 text-base font-bold"
        data-testid="branding-save-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        {saving ? 'Saving...' : 'Save Branding'}
      </Button>
    </motion.div>
  );
}
